import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import {
	Check,
	ChevronRight,
	Download,
	History,
	Loader2,
	Pencil,
	Share2,
	Trash2,
	Upload,
} from 'lucide-react'
import { Sheet } from 'components/ui/sheet'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Badge } from 'components/ui/badge'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from 'components/ui/table'
import {
	fetchOne,
	fetchVersions,
	renameDocument,
	softDeleteDocument,
} from 'slices/documentLibrarySlice'
import {
	getCurrentVersion,
	type DocumentLibraryState,
	type LibraryDocument,
	type UserSummary,
} from '../types/documentLibrary.types'
import { downloadAndSave } from '../services/libraryDocumentService'
import DocumentAvatar from './DocumentAvatar'
import PopupCloseButton from 'components/PopupCloseButton'

interface DocumentEditSheetProps {
	open: boolean
	onClose: () => void
	documentId: string | null
	onUploadNewVersion?: (documentId: string) => void
	onShare?: (documentId: string, versionId?: string) => void
	onShowVersions?: (documentId: string) => void
}

const ownerLabel = (
	user: LibraryDocument['owner'] | undefined,
): string => {
	if (!user) return ''
	if (typeof user === 'string') return ''
	const u = user as UserSummary
	const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
	return full || u.email || ''
}

const formatBytes = (n: number | undefined): string => {
	if (!n && n !== 0) return ''
	if (n < 1024) return `${n} B`
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
	if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
	return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

const formatDate = (iso: string | null | undefined): string =>
	iso ? moment(iso).format('MMM D, YYYY h:mm A') : ''

const noop = () => {}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({
	label,
	value,
}) => (
	<div className="flex flex-col gap-0.5">
		<div className="text-[11px] uppercase tracking-wide text-gray-500">
			{label}
		</div>
		<div className="text-sm text-gray-900 break-words">
			{value || <span className="text-gray-400">—</span>}
		</div>
	</div>
)

const DocumentEditSheet: React.FC<DocumentEditSheetProps> = ({
	open,
	onClose,
	documentId,
	onUploadNewVersion = noop,
	onShare = noop,
	onShowVersions = noop,
}) => {
	const dispatch = useDispatch<any>()
	const document = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			documentId ? state.libraryDocuments.entities[documentId] : undefined,
	)
	const versions = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			documentId
				? state.libraryDocuments.versionsByDocument[documentId] || []
				: [],
	)
	const userInfo = useSelector((state: any) => state.userLogin?.userInfo)
	const currentVersion = getCurrentVersion(document)

	const [nameDraft, setNameDraft] = useState(document?.libraryName || '')
	const [renaming, setRenaming] = useState(false)
	const [renameError, setRenameError] = useState<string | null>(null)
	const [downloading, setDownloading] = useState(false)
	const [deleting, setDeleting] = useState(false)

	// Reset when documentId changes; load doc + versions if missing.
	useEffect(() => {
		if (!open) return
		setNameDraft(document?.libraryName || '')
		setRenameError(null)
		setRenaming(false)
		setDownloading(false)
		setDeleting(false)
		if (documentId && !document) dispatch(fetchOne({ id: documentId }))
		if (documentId && versions.length === 0)
			dispatch(fetchVersions({ id: documentId }))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, documentId])

	// Keep nameDraft in sync after the slice updates the document (rename
	// fulfilled, or fetchOne resolved).
	useEffect(() => {
		if (!renaming) {
			setNameDraft(document?.libraryName || '')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [document?.libraryName])

	const ownerId = useMemo(() => {
		if (!document) return null
		const owner = document.owner
		if (!owner) return null
		if (typeof owner === 'string') return owner
		return (owner as UserSummary)._id || null
	}, [document])
	const isDocOwner =
		!!userInfo?._id && !!ownerId && String(userInfo._id) === String(ownerId)

	const recentVersions = useMemo(
		() => [...versions].slice(0, 5),
		[versions],
	)

	const trimmedName = nameDraft.trim()
	const nameDirty =
		!!document &&
		trimmedName.length > 0 &&
		trimmedName !== document.libraryName

	const handleSaveName = useCallback(async () => {
		if (!documentId || !nameDirty) return
		setRenaming(true)
		setRenameError(null)
		try {
			await dispatch(
				renameDocument({ id: documentId, libraryName: trimmedName }),
			).unwrap()
		} catch (e: any) {
			setRenameError(typeof e === 'string' ? e : e?.message || 'Rename failed')
		} finally {
			setRenaming(false)
		}
	}, [documentId, nameDirty, trimmedName, dispatch])

	const handleCancelRename = useCallback(() => {
		setNameDraft(document?.libraryName || '')
		setRenameError(null)
	}, [document?.libraryName])

	const handleDownloadCurrent = useCallback(async () => {
		if (!documentId || !currentVersion || downloading) return
		setDownloading(true)
		try {
			await downloadAndSave(
				{
					id: documentId,
					versionId: currentVersion._id,
					filename: currentVersion.originalFilename,
				},
				{
					headers: {
						Authorization: `Bearer ${userInfo?.token || ''}`,
					},
				},
			)
		} finally {
			setDownloading(false)
		}
	}, [documentId, currentVersion, downloading, userInfo?.token])

	const handleSoftDelete = useCallback(async () => {
		if (!documentId || deleting) return
		const ok = await toast.confirm({
			title: 'Delete document',
			message:
				'Delete this document? It will be hidden from the library; older versions remain in the audit trail.',
		})
		if (!ok) return
		setDeleting(true)
		try {
			await dispatch(softDeleteDocument({ id: documentId })).unwrap()
			onClose()
		} catch {
			setDeleting(false)
		}
	}, [documentId, deleting, dispatch, onClose])

	if (!documentId) return null

	return (
		<Sheet open={open} onClose={onClose} side="right" className="!w-[480px]">
			<div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
				<div className="flex items-center gap-3 min-w-0">
					<DocumentAvatar
						filename={
							currentVersion?.originalFilename || document?.libraryName || ''
						}
						size={36}
						tooltipText="Current version"
					/>
					<div className="min-w-0">
						<div className="text-[11px] uppercase tracking-wide text-gray-500">
							Document
						</div>
						<div
							className="text-sm font-semibold text-gray-900 truncate"
							title={document?.libraryName}
						>
							{document?.libraryName || 'Loading…'}
						</div>
					</div>
				</div>
				<PopupCloseButton onClose={onClose} />
			</div>

			<div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-6">
				{/* Rename */}
				<section className="flex flex-col gap-2">
					<Label
						htmlFor="library-name-edit"
						className="text-xs text-gray-700 inline-flex items-center gap-1"
					>
						<Pencil className="w-3 h-3" />
						Library Name
					</Label>
					<div className="flex items-center gap-2">
						<Input
							id="library-name-edit"
							value={nameDraft}
							onChange={e => setNameDraft(e.target.value)}
							disabled={renaming || !document}
							className={
								renameError
									? 'border-red-400 focus:border-red-500 focus:ring-red-500'
									: ''
							}
						/>
						{nameDirty && (
							<>
								<Button
									type="button"
									variant="success"
									onClick={handleSaveName}
									disabled={renaming}
									className="gap-1 whitespace-nowrap"
								>
									{renaming ? (
										<Loader2 className="w-4 h-4 animate-spin" />
									) : (
										<Check className="w-4 h-4" />
									)}
									Save
								</Button>
								<Button
									type="button"
									variant="ghost"
									onClick={handleCancelRename}
									disabled={renaming}
								>
									Cancel
								</Button>
							</>
						)}
					</div>
					{renameError && (
						<div className="text-xs text-red-600">{renameError}</div>
					)}
				</section>

				{/* Metadata */}
				<section className="grid grid-cols-2 gap-x-4 gap-y-3">
					<Field label="Owner" value={ownerLabel(document?.owner)} />
					<Field
						label="Current version"
						value={
							currentVersion
								? `v${currentVersion.versionNumber}`
								: '—'
						}
					/>
					<Field
						label="Filename"
						value={currentVersion?.originalFilename}
					/>
					<Field
						label="Size"
						value={formatBytes(currentVersion?.byteSize)}
					/>
					<Field label="Type" value={currentVersion?.mimeType} />
					<Field
						label="File date"
						value={
							currentVersion?.fileTrueCreatedAt
								? moment(currentVersion.fileTrueCreatedAt).format('MMM D, YYYY')
								: ''
						}
					/>
					<Field
						label="Uploaded"
						value={formatDate(currentVersion?.uploadedAt)}
					/>
					<Field
						label="Last updated"
						value={formatDate(document?.updatedAt)}
					/>
					<div className="col-span-2">
						<Field
							label="Preview status"
							value={
								currentVersion?.previewStatus === 'ready' ? (
									<Badge className="bg-green-100 text-green-700 hover:bg-green-100">
										Ready
									</Badge>
								) : currentVersion?.previewStatus === 'failed' ? (
									<Badge className="bg-red-100 text-red-700 hover:bg-red-100">
										Failed{currentVersion.previewError ? ` — ${currentVersion.previewError}` : ''}
									</Badge>
								) : (
									<Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
										Pending
									</Badge>
								)
							}
						/>
					</div>
				</section>

				{/* Recent versions */}
				<section className="flex flex-col gap-2">
					<div className="flex items-center justify-between">
						<Label className="text-xs text-gray-700 inline-flex items-center gap-1">
							<History className="w-3 h-3" />
							Recent versions
						</Label>
						<button
							type="button"
							onClick={() => onShowVersions(documentId)}
							className="text-xs text-[#0080fe] hover:underline inline-flex items-center gap-0.5 bg-transparent border-0 p-0 cursor-pointer"
						>
							View full history
							<ChevronRight className="w-3 h-3" />
						</button>
					</div>
					{recentVersions.length === 0 ? (
						<div className="text-xs text-gray-500 py-3">
							No versions cached yet.
						</div>
					) : (
						<div className="rounded-lg border border-gray-200 overflow-hidden">
							<Table className="w-full">
								<TableHeader>
									<TableRow className="border-b border-gray-200 hover:bg-transparent">
										<TableHead className="text-[11px] font-semibold text-gray-700 h-8 w-12 text-center">
											v
										</TableHead>
										<TableHead className="text-[11px] font-semibold text-gray-700 h-8">
											Owner
										</TableHead>
										<TableHead className="text-[11px] font-semibold text-gray-700 h-8 w-24">
											Date
										</TableHead>
										<TableHead className="text-[11px] font-semibold text-gray-700 h-8 w-12 text-center">
											Cur
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{recentVersions.map(v => (
										<TableRow key={v._id} className="h-8 hover:bg-gray-50">
											<TableCell className="py-1 text-xs text-center">
												{v.versionNumber}
											</TableCell>
											<TableCell className="py-1 text-xs">
												{ownerLabel(v.uploadedBy)}
											</TableCell>
											<TableCell className="py-1 text-xs whitespace-nowrap">
												{v.fileTrueCreatedAt
													? moment(v.fileTrueCreatedAt).format('MM/DD/YY')
													: ''}
											</TableCell>
											<TableCell className="py-1 text-xs text-center">
												{v.isCurrent ? (
													<Check className="inline w-3 h-3 text-green-600" />
												) : null}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</section>

				{/* Actions */}
				<section className="flex flex-col gap-2">
					<Label className="text-xs text-gray-700">Actions</Label>
					<div className="grid grid-cols-2 gap-2">
						<Button
							type="button"
							variant="primary"
							onClick={() => onUploadNewVersion(documentId)}
							className="gap-1 justify-start"
						>
							<Upload className="w-4 h-4" />
							Upload new version
						</Button>
						<Button
							type="button"
							variant="ghost"
							onClick={() =>
								onShare(documentId, currentVersion?._id)
							}
							className="gap-1 justify-start"
						>
							<Share2 className="w-4 h-4" />
							Share
						</Button>
						<Button
							type="button"
							variant="ghost"
							onClick={handleDownloadCurrent}
							disabled={!currentVersion || downloading}
							className="gap-1 justify-start"
						>
							{downloading ? (
								<Loader2 className="w-4 h-4 animate-spin" />
							) : (
								<Download className="w-4 h-4" />
							)}
							{downloading ? 'Downloading…' : 'Download current'}
						</Button>
						{isDocOwner ? (
							<Button
								type="button"
								variant="ghost-red"
								onClick={handleSoftDelete}
								disabled={deleting}
								className="gap-1 justify-start"
							>
								<Trash2 className="w-4 h-4" />
								{deleting ? 'Deleting…' : 'Delete document'}
							</Button>
						) : (
							<div />
						)}
					</div>
				</section>
			</div>
		</Sheet>
	)
}

export default DocumentEditSheet
